/**
 * AlertContext - Global Alert State Management
 *
 * This context manages:
 * 1. Persistent WebSocket connection for receiving alerts
 * 2. Global alert state (accessible from any component)
 * 3. Toast notifications (popup in top-right corner)
 * 4. Browser notifications (system notifications)
 * 5. Alert center data (notification history)
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import websocketManager from '../services/websocketManager';
import { logger } from '../utils/logger';
import { ultronTuringApi } from '../services/ultronTuringApi';

// Alert type definition
export interface CenterAlert {
  id: string;
  type: 'critical' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  deviceSn?: string;
  timestamp: string;
  acknowledged?: boolean;
  severity?: string;
  metric?: string;  // Alert metric type (power, temperature, etc.)
}

// Context type definition
interface AlertContextType {
  alerts: CenterAlert[];
  unreadCount: number;
  isConnected: boolean;
  isLoading: boolean;
  dismissAlert: (id: string) => void;
  acknowledgeAlert: (id: string) => void;
  clearAllAlerts: () => void;
  requestNotificationPermission: () => Promise<NotificationPermission>;
  reloadNotifications: () => Promise<void>;
  markNotificationRead: (id: string) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

// Custom hook to use AlertContext
export const useAlertContext = () => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error('useAlertContext must be used within an AlertProvider');
  }
  return context;
};

interface AlertProviderProps {
  children: React.ReactNode;
  maxAlerts?: number;
}

// localStorage key for dismissed alert IDs
const DISMISSED_ALERTS_KEY = 'ultron-dismissed-alerts';
const MAX_DISMISSED_ALERTS = 200; // Limit to prevent localStorage bloat

// Helper: Get dismissed alert IDs from localStorage
const getDismissedAlertIds = (): Set<string> => {
  try {
    const stored = localStorage.getItem(DISMISSED_ALERTS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return new Set(Array.isArray(parsed) ? parsed : []);
    }
  } catch (e) {
    // Ignore parse errors
  }
  return new Set();
};

// Helper: Save dismissed alert IDs to localStorage
const saveDismissedAlertIds = (ids: Set<string>) => {
  try {
    // Keep only the most recent IDs to prevent bloat
    const idsArray = Array.from(ids).slice(-MAX_DISMISSED_ALERTS);
    localStorage.setItem(DISMISSED_ALERTS_KEY, JSON.stringify(idsArray));
  } catch (e) {
    // Ignore storage errors
  }
};

export const AlertProvider: React.FC<AlertProviderProps> = ({
  children,
  maxAlerts = 50
}) => {
  const [alerts, setAlerts] = useState<CenterAlert[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Track dismissed alert IDs (persisted to localStorage)
  const dismissedAlertIdsRef = useRef<Set<string>>(getDismissedAlertIds());

  // Use ref for handleAlert to avoid re-creating WebSocket on every render
  const handleAlertRef = useRef<((data: any) => void) | undefined>(undefined);

  // Check authentication status on mount and when storage changes
  useEffect(() => {
    const checkAuth = () => {
      // Check for OAuth tokens in both localStorage and sessionStorage
      const sessionTokens = sessionStorage.getItem('ultron_tokens');
      const localTokens = localStorage.getItem('ultron_tokens');
      const hasTokens = !!(sessionTokens || localTokens);

      if (hasTokens) {
        try {
          const tokens = JSON.parse(sessionTokens || localTokens || '{}');
          setIsAuthenticated(!!tokens.accessToken);
        } catch {
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
    };

    // Initial check
    checkAuth();

    // Listen for storage changes (cross-tab sync)
    const handleStorageChange = () => {
      checkAuth();
    };

    // Listen for custom auth event (same-tab)
    const handleAuthChange = () => {
      checkAuth();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-changed', handleAuthChange);

    // Also check periodically in case tokens are set after initial mount
    const interval = setInterval(checkAuth, 2000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-changed', handleAuthChange);
      clearInterval(interval);
    };
  }, []);

  // Convert raw alert data to CenterAlert format
  const eventToAlert = useCallback((event: any): CenterAlert | null => {
    // Handle battery alerts specially - generate stable ID based on rule ID + date
    // This prevents the same battery alert from showing multiple times on the same day
    const isBatteryAlert = event.type === 'battery_alert' || event.alertType === 'battery' || 
                           event.metric === 'battery' || event.name?.includes('電池');
    
    if (isBatteryAlert) {
      // Generate stable ID for battery alerts: ruleId + today's date
      // This way, the same battery alert on the same day will have the same ID
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const ruleId = event.ruleId || event.alertId || event.id || 'battery-alert';
      const stableId = `battery-${ruleId}-${today}`;
      
      return {
        id: stableId,
        type: 'warning',
        title: event.name || event.title || '電池電量警報',
        message: event.message || `${event.deviceCount || 0} 個設備電量低於 ${event.threshold || 20}%`,
        timestamp: event.timestamp || new Date().toISOString(),
        severity: event.severity || 'warning',
        metric: 'battery'
      };
    }
    
    // Handle Turing Server alert format (type: 'alert', alertType: 'triggered'/'recovered')
    // Also support legacy format (type: 'triggered'/'recovered')
    const isNewFormat = event.type === 'alert' && event.alertType;
    const isLegacyFormat = event.type === 'triggered' || event.type === 'recovered';

    if (isNewFormat || isLegacyFormat) {
      // Support both field naming conventions
      const alertId = event.ruleId || event.alertId;
      const alertName = event.ruleName || event.alertName;
      const deviceSn = event.device || event.deviceSn;
      const { metric, condition, threshold, currentValue, severity } = event;

      const isRecovered = event.alertType === 'recovered' || event.type === 'recovered';
      const alertType: CenterAlert['type'] =
        isRecovered ? 'success' :
        severity === 'critical' || severity === 'major' ? 'critical' : 'warning';

      const title = isRecovered
        ? `${alertName} - Recovered`
        : `${alertName}`;

      // Build display device name (priority: nickname > displayName > deviceName > deviceSn)
      // First check if server provided name, then lookup from local importedDevices
      let displayDevice = event.nickname || event.displayName || event.deviceName || '';
      if (!displayDevice && deviceSn) {
        // Lookup from localStorage importedDevices
        try {
          const importedDevices = JSON.parse(localStorage.getItem('importedDevices') || '[]');
          const device = importedDevices.find((d: any) => d.sn === deviceSn || d.deviceSN === deviceSn);
          if (device) {
            displayDevice = device.nickname || device.displayName || device.name || deviceSn;
          } else {
            displayDevice = deviceSn;
          }
        } catch {
          displayDevice = deviceSn;
        }
      }

      // Build condition symbol for display
      const conditionMap: Record<string, string> = {
        'above': '>',
        'below': '<',
        'equals': '=',
        '>=': '≥',
        '<=': '≤',
        'gt': '>',
        'lt': '<',
        'eq': '=',
        'gte': '≥',
        'lte': '≤'
      };
      const conditionSymbol = conditionMap[condition] || condition || '>';

      const message = isRecovered
        ? `${displayDevice}: ${metric} returned to normal (${currentValue})`
        : `${displayDevice}: ${metric} ${conditionSymbol} ${threshold} (current: ${currentValue})`;

      return {
        id: alertId || `alert-${Date.now()}`,
        type: alertType,
        title,
        message,
        deviceSn,
        timestamp: event.timestamp || new Date().toISOString(),
        severity,
        metric  // Include metric for board-type filtering
      };
    }

    // Legacy event format support
    const { eventType, deviceSn, message, timestamp, priority, critical } = event;

    let type: CenterAlert['type'] = 'info';
    let title = 'System Event';

    if (critical || priority === 1) {
      type = 'critical';
      title = 'Critical Alert';
    } else if (eventType?.includes('warning') || eventType?.includes('threshold')) {
      type = 'warning';
      title = 'Warning';
    } else if (eventType?.includes('success') || eventType?.includes('online')) {
      type = 'success';
      title = 'Success';
    }

    if (message) {
      return {
        id: event.id || `alert-${Date.now()}`,
        type,
        title,
        message,
        deviceSn,
        timestamp: timestamp || new Date().toISOString()
      };
    }
    return null;
  }, []);

  // Show Toast notification (popup in top-right corner)
  const showToast = useCallback((alert: any) => {
    const severity = alert.severity || alert.type || 'info';
    const isCritical = severity === 'critical' || severity === 'major';
    const isWarning = severity === 'warning';
    const isSuccess = alert.type === 'recovered' || severity === 'success';

    // Severity colors for left border
    const getSeverityColor = () => {
      if (isSuccess) return '#10b981'; // green
      if (isCritical) return '#ef4444'; // red
      if (isWarning) return '#f59e0b'; // amber
      return '#3b82f6'; // blue
    };

    const severityColor = getSeverityColor();

    // Format the alert message
    // Clean up alert name: remove "Alert: " prefix and " triggered" suffix if present
    let alertName = alert.alertName || alert.ruleName || alert.title || 'Alert';
    if (alertName.startsWith('Alert: ')) {
      alertName = alertName.replace('Alert: ', '');
    }
    if (alertName.endsWith(' triggered')) {
      alertName = alertName.replace(' triggered', '');
    }
    const deviceInfo = alert.deviceName || alert.deviceSn || '';
    
    // Build message based on condition and metric type
    let messageContent = '';
    if (alert.condition === 'changed' || alert.condition === 'neq') {
      // State change: show friendly message
      const metricLabels: Record<string, string> = {
        'power_state': '設備狀態',
        'online': '連線狀態',
        'presence': '人員感應'
      };
      const metricLabel = metricLabels[alert.metric] || alert.metric;
      messageContent = deviceInfo
        ? `${deviceInfo}: ${metricLabel}已變化`
        : `${metricLabel}已變化`;
    } else if (alert.currentValue === 'N/A' || alert.currentValue === null || (alert.currentValue === undefined && alert.metric)) {
      // Invalid currentValue or state-based condition: show condition-based message
      const conditionLabels: Record<string, string> = {
        'on': '已開啟',
        'off': '已關閉',
        'online': '已上線',
        'offline': '已離線',
        'detected': '偵測到',
        'not_detected': '未偵測到'
      };
      const conditionLabel = conditionLabels[alert.condition] || '';
      if (conditionLabel) {
        const metricLabels: Record<string, string> = {
          'power_state': '設備',
          'online': '設備',
          'presence': '人員'
        };
        const metricLabel = metricLabels[alert.metric] || '';
        messageContent = deviceInfo
          ? `${deviceInfo}: ${metricLabel}${conditionLabel}`
          : `${metricLabel}${conditionLabel}`;
      } else {
        // Fallback: show metric and condition
        const metricInfo = alert.metric ? `${alert.metric}` : '';
        messageContent = deviceInfo
          ? `${deviceInfo}: ${metricInfo} ${alert.condition || ''}`
          : `${metricInfo} ${alert.condition || ''}`;
      }
    } else if (alert.metric && alert.currentValue !== undefined) {
      // Numeric metrics: show metric, value, and threshold
      const metricInfo = alert.metric ? `${alert.metric}` : '';
      const valueInfo = `${alert.currentValue}`;
      const thresholdInfo = alert.threshold !== undefined ? `${alert.condition || '>'} ${alert.threshold}` : '';
      messageContent = deviceInfo
        ? `${deviceInfo}: ${metricInfo} ${valueInfo}${thresholdInfo ? ` (${thresholdInfo})` : ''}`
        : `${metricInfo} ${valueInfo}${thresholdInfo ? ` (${thresholdInfo})` : ''}`;
    } else if (alert.message) {
      // Use provided message
      messageContent = alert.message;
    } else {
      // Last resort
      messageContent = deviceInfo || '警報已觸發';
    }
    
    // Legacy format for backward compatibility (used in toast HTML)
    const metricInfo = alert.metric ? `${alert.metric}` : '';
    const valueInfo = alert.currentValue !== undefined && alert.currentValue !== 'N/A' && alert.currentValue !== null ? `${alert.currentValue}` : '';
    const thresholdInfo = alert.threshold !== undefined ? `${alert.condition || '>'} ${alert.threshold}` : '';

    // Create toast container
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 80px;
      right: 24px;
      width: 360px;
      background: #1f2937;
      border-radius: 8px;
      border-left: 4px solid ${severityColor};
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3), 0 4px 10px rgba(0, 0, 0, 0.2);
      z-index: 9999;
      overflow: hidden;
      transform: translateX(400px);
      transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    toast.innerHTML = `
      <div style="padding: 14px 16px; display: flex; align-items: flex-start; gap: 12px;">
        <div style="flex: 1; min-width: 0;">
          <div style="
            font-size: 14px;
            font-weight: 600;
            color: #f9fafb;
            margin-bottom: 4px;
            line-height: 1.3;
          ">${alertName}</div>
          <div style="
            font-size: 13px;
            color: #9ca3af;
            line-height: 1.4;
          ">
            ${messageContent ? `<span style="color: #d1d5db;">${messageContent}</span>` : ''}
          </div>
        </div>
        <button onclick="this.closest('div[style*=position]').remove()" style="
          background: transparent;
          border: none;
          width: 24px;
          height: 24px;
          border-radius: 4px;
          color: #6b7280;
          font-size: 18px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.2s;
        " onmouseover="this.style.background='#374151';this.style.color='#f9fafb'" onmouseout="this.style.background='transparent';this.style.color='#6b7280'">&times;</button>
      </div>
      <div style="height: 2px; background: #374151;">
        <div class="toast-progress" style="
          height: 100%;
          background: ${severityColor};
          width: 100%;
          transition: width ${isCritical ? '10s' : '6s'} linear;
        "></div>
      </div>
    `;

    document.body.appendChild(toast);

    // Animate in
    requestAnimationFrame(() => {
      toast.style.transform = 'translateX(0)';
      // Start progress bar animation
      const progress = toast.querySelector('.toast-progress') as HTMLElement;
      if (progress) {
        requestAnimationFrame(() => {
          progress.style.width = '0%';
        });
      }
    });

    // Auto-dismiss after delay
    const dismissDelay = isCritical ? 10000 : 6000;
    setTimeout(() => {
      toast.style.transform = 'translateX(400px)';
      setTimeout(() => toast.remove(), 300);
    }, dismissDelay);

    // Play sound for critical alerts
    if (isCritical) {
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.1;
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.2);
      } catch (e) {
        // Audio not available
      }
    }
  }, []);

  // Show Browser Notification (system notification)
  const showBrowserNotification = useCallback((alert: any) => {
    // Debug: log raw alert data
    logger.log('🔔 [AlertContext] showBrowserNotification raw data:', JSON.stringify(alert, null, 2));

    if (!('Notification' in window)) {
      logger.log('[AlertContext] Browser does not support notifications');
      return;
    }

    if (Notification.permission !== 'granted') {
      logger.log('[AlertContext] Notification permission not granted');
      return;
    }

    const isCritical = alert.severity === 'critical' || alert.severity === 'major';
    const isRecovered = alert.type === 'recovered';

    // Build clear title: ruleName (Turing Server format) or alertName or fallback
    const title = alert.ruleName || alert.alertName || alert.title || 'Alert';

    // Build informative body message
    let body = '';
    // Support multiple field names (priority: nickname > displayName > deviceName > device/deviceSn)
    // First check if server provided name, then lookup from local importedDevices
    const deviceSn = alert.device || alert.deviceSn || '';
    let deviceDisplay = alert.nickname || alert.displayName || alert.deviceName || '';
    if (!deviceDisplay && deviceSn) {
      try {
        const importedDevices = JSON.parse(localStorage.getItem('importedDevices') || '[]');
        const device = importedDevices.find((d: any) => d.sn === deviceSn || d.deviceSN === deviceSn);
        if (device) {
          deviceDisplay = device.nickname || device.displayName || device.name || deviceSn;
        } else {
          deviceDisplay = deviceSn;
        }
      } catch {
        deviceDisplay = deviceSn;
      }
    }

    if (isRecovered) {
      // Recovered: "設備名稱: 指標已恢復正常 (當前值: xxx)"
      body = deviceDisplay
        ? `${deviceDisplay}: ${alert.metric || ''} 已恢復正常`
        : `${alert.metric || ''} 已恢復正常`;
      if (alert.currentValue !== undefined) {
        body += ` (當前: ${alert.currentValue})`;
      }
    } else if (alert.metric && alert.currentValue !== undefined) {
      // Handle state-change condition specially
      if (alert.condition === 'changed' || alert.condition === 'neq') {
        // State change: show friendly message
        const metricLabels: Record<string, string> = {
          'power_state': '設備狀態',
          'online': '連線狀態',
          'presence': '人員感應'
        };
        const metricLabel = metricLabels[alert.metric] || alert.metric;
        body = deviceDisplay
          ? `${deviceDisplay}: ${metricLabel}已變化`
          : `${metricLabel}已變化`;
      } else if (alert.currentValue === 'N/A' || alert.currentValue === null || alert.currentValue === undefined) {
        // Invalid currentValue: show condition-based message
        const conditionLabels: Record<string, string> = {
          'on': '已開啟',
          'off': '已關閉',
          'online': '已上線',
          'offline': '已離線',
          'detected': '偵測到',
          'not_detected': '未偵測到'
        };
        const conditionLabel = conditionLabels[alert.condition] || '';
        if (conditionLabel) {
          const metricLabels: Record<string, string> = {
            'power_state': '設備',
            'online': '設備',
            'presence': '人員'
          };
          const metricLabel = metricLabels[alert.metric] || '';
          body = deviceDisplay
            ? `${deviceDisplay}: ${metricLabel}${conditionLabel}`
            : `${metricLabel}${conditionLabel}`;
        } else {
          // Fallback
          body = deviceDisplay
            ? `${deviceDisplay}: ${alert.metric} ${alert.condition}`
            : `${alert.metric} ${alert.condition}`;
        }
      } else {
        // Numeric metrics: "設備名稱: 指標 數值 (閾值: xxx)"
        const conditionMap: Record<string, string> = {
          'above': '>',
          'below': '<',
          'equals': '=',
          '>=': '≥',
          '<=': '≤',
          'gt': '>',
          'lt': '<',
          'eq': '=',
          'gte': '≥',
          'lte': '≤'
        };
        const conditionSymbol = conditionMap[alert.condition] || alert.condition || '>';

        body = deviceDisplay
          ? `${deviceDisplay}: ${alert.metric} ${alert.currentValue}`
          : `${alert.metric}: ${alert.currentValue}`;

        if (alert.threshold !== undefined) {
          body += ` (閾值: ${conditionSymbol} ${alert.threshold})`;
        }
      }
    } else if (alert.message) {
      // Fallback to message field - clean up redundant "Alert: xxx triggered" format
      let cleanMessage = alert.message;
      if (cleanMessage.startsWith('Alert: ') && cleanMessage.endsWith(' triggered')) {
        // Already have ruleName as title, just show "已觸發" or severity
        const severityText = alert.severity === 'critical' ? '嚴重警報' :
                            alert.severity === 'warning' ? '警告' : '通知';
        body = `${severityText}已觸發`;
      } else {
        body = cleanMessage;
      }
    } else {
      // Last resort fallback
      body = deviceDisplay || '警報已觸發';
    }

    // Get user-uploaded logo from localStorage, fallback to default
    const customLogo = localStorage.getItem('ultron-company-logo');
    const notificationIcon = customLogo || '/ultron-logo.png';

    try {
      const notification = new Notification(title, {
        body,
        icon: notificationIcon,
        requireInteraction: isCritical,
        tag: alert.alertId || alert.id,
        silent: false
      });

      // Auto-close non-critical notifications
      if (!isCritical) {
        setTimeout(() => notification.close(), 8000);
      }

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (e) {
      logger.error('[AlertContext] Failed to show browser notification:', e);
    }
  }, []);

  // Request notification permission
  const requestNotificationPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!('Notification' in window)) {
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission;
    }

    return Notification.permission;
  }, []);

  // Handle incoming alert
  const handleAlert = useCallback((data: any) => {
    logger.log('🔔 [AlertContext] Alert received:', data);

    const centerAlert = eventToAlert(data);
    if (centerAlert) {
      // Check if this alert was previously dismissed
      if (dismissedAlertIdsRef.current.has(centerAlert.id)) {
        return; // Skip dismissed alert
      }

      // Add to alerts list
      setAlerts(prev => {
        // Avoid duplicates
        const exists = prev.some(a => a.id === centerAlert.id);
        if (exists) return prev;
        return [centerAlert, ...prev].slice(0, maxAlerts);
      });

      // Check notification channel preferences
      // Server may send notificationChannels array with preferences
      const channels = data.notificationChannels as string[] | undefined;

      // Determine which notifications to show based on channel preferences
      // For backwards compatibility: if no channels specified or has 'websocket', show both
      const hasLegacyWebsocket = !channels || channels.includes('websocket');
      const shouldShowToast = hasLegacyWebsocket || channels?.includes('toast');
      const shouldShowSystem = hasLegacyWebsocket || channels?.includes('system');

      // Show Toast notification (in-app popup)
      if (shouldShowToast) {
        showToast(data);
      }

      // Show Browser Notification (system notification)
      if (shouldShowSystem) {
        showBrowserNotification(data);
      }
    }
  }, [eventToAlert, showToast, showBrowserNotification, maxAlerts]);

  // Update ref when handleAlert changes
  useEffect(() => {
    handleAlertRef.current = handleAlert;
  }, [handleAlert]);

  // Track WebSocket connection state
  const [isConnected, setIsConnected] = useState(false);

  // Use global WebSocket manager instead of creating a new connection
  useEffect(() => {
    if (!isAuthenticated) return;

    // Connect to global WebSocket
    websocketManager.connect().then(() => {
      setIsConnected(websocketManager.getState().isConnected);
    });

    // Register alert callback
    const unsubscribeAlert = websocketManager.onAlert((data: any) => {
      handleAlertRef.current?.(data);
    });

    // Register connect callback
    const unsubscribeConnect = websocketManager.onConnect(() => {
      setIsConnected(true);
      logger.log('✅ [AlertContext] Global WebSocket connected');
    });

    return () => {
      unsubscribeAlert();
      unsubscribeConnect();
    };
  }, [isAuthenticated]);

  // Dismiss alert (remove from list and persist to localStorage)
  const dismissAlert = useCallback((alertId: string) => {
    // Add to dismissed IDs and persist
    dismissedAlertIdsRef.current.add(alertId);
    saveDismissedAlertIds(dismissedAlertIdsRef.current);
    
    // Remove from current alerts list
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  }, []);

  // Acknowledge alert (mark as read but keep in list)
  const acknowledgeAlert = useCallback((alertId: string) => {
    setAlerts(prev =>
      prev.map(a =>
        a.id === alertId ? { ...a, acknowledged: true } : a
      )
    );
  }, []);

  // Clear all alerts (mark all as dismissed and persist)
  const clearAllAlerts = useCallback(() => {
    // Mark all current alerts as dismissed
    setAlerts(prev => {
      prev.forEach(alert => {
        dismissedAlertIdsRef.current.add(alert.id);
      });
      if (prev.length > 0) {
        saveDismissedAlertIds(dismissedAlertIdsRef.current);
      }
      return [];
    });
  }, []);

  // Notification type from API response
  // Supports both formats: some APIs use 'timestamp', others use 'createdAt'
  interface NotificationFromApi {
    id: string;
    type: string;
    title: string;
    message: string;
    deviceSn?: string;
    timestamp?: string;     // Optional - some responses use this
    createdAt?: string;     // Optional - some responses use this
    read: boolean;
    severity?: string;
    metric?: string;
  }

  // Format System log messages to be more human-readable
  const formatSystemMessage = (title: string, message: string): string => {
    // Check if this is a System log message (connection status)
    if (title === 'System' || title.toLowerCase().includes('system')) {
      // Check for disconnect indicators
      if (message.toLowerCase().includes('disconnect') || 
          message.toLowerCase().includes('timeout') ||
          message.toLowerCase().includes('mqtt_keep_alive_timeout')) {
        return '已離線 (Disconnected)';
      }
      // Check for connect indicators or generic system messages with serviceName
      if (message.includes('serviceName:') || 
          message.toLowerCase().includes('connect') ||
          message.includes('severity:')) {
        // If it contains disconnect-related info, it's disconnected
        if (message.toLowerCase().includes('disconnect')) {
          return '已離線 (Disconnected)';
        }
        // Otherwise assume connected
        return '已連線 (Connected)';
      }
    }
    return message;
  };

  // Load notifications from Firestore via MCP API
  const loadNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await ultronTuringApi.queryNotifications({ action: 'list_all', limit: 50 });
      
      // Support both response formats: result.data.notifications and result.notifications
      const notificationsData = result.data?.notifications || (result as any).notifications;
      
      if (result.success && notificationsData) {
        const notifications = notificationsData.map((n: NotificationFromApi) => ({
          id: n.id,
          type: (n.severity === 'critical' || n.severity === 'major' ? 'critical' : 
                 n.severity === 'warning' ? 'warning' : 
                 n.type === 'success' ? 'success' : 'info') as CenterAlert['type'],
          title: n.title,
          message: formatSystemMessage(n.title || '', n.message || ''),
          deviceSn: n.deviceSn,
          timestamp: n.timestamp || n.createdAt || '',  // Use empty string if no timestamp
          acknowledged: n.read,
          severity: n.severity,
          metric: n.metric
        }));
        
        // Filter out dismissed alerts
        const filteredNotifications = notifications.filter(
          (n: CenterAlert) => !dismissedAlertIdsRef.current.has(n.id)
        );
        
        // Use API response as source of truth
        // Only preserve very recent alerts (within last 10 seconds) that aren't in API response
        // These are likely WebSocket alerts that arrived during the API call
        const now = Date.now();
        const RECENT_THRESHOLD = 10000; // 10 seconds
        
        setAlerts(prev => {
          // Create a map of loaded notification IDs for quick lookup
          const loadedIds = new Set(filteredNotifications.map((n: CenterAlert) => n.id));
          
          // Only keep alerts that:
          // 1. Are NOT in the API response (not duplicates)
          // 2. Have a timestamp within the last 10 seconds OR have no valid timestamp
          //    (alerts without timestamps are preserved since we can't verify their age)
          const recentWebSocketAlerts = prev.filter(a => {
            if (loadedIds.has(a.id)) return false; // Already in API response
            if (!a.timestamp) return true; // Preserve alerts without timestamp (can't verify age)
            const alertTime = new Date(a.timestamp).getTime();
            if (isNaN(alertTime)) return true; // Preserve alerts with invalid timestamp
            return (now - alertTime) < RECENT_THRESHOLD;
          });
          
          // Combine: recent WebSocket alerts + API notifications
          // Sort by timestamp descending
          const merged = [...recentWebSocketAlerts, ...filteredNotifications]
            .sort((a, b) => {
              const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
              const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
              const validTimeA = isNaN(timeA) ? 0 : timeA;
              const validTimeB = isNaN(timeB) ? 0 : timeB;
              return validTimeB - validTimeA;
            })
            .slice(0, maxAlerts);
          
          return merged;
        });
        logger.log('📥 [AlertContext] Loaded notifications from Firestore:', filteredNotifications.length);
      }
    } catch (error) {
      logger.error('[AlertContext] Failed to load notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [maxAlerts]);

  // Reload notifications (exposed for manual refresh)
  const reloadNotifications = useCallback(async () => {
    await loadNotifications();
  }, [loadNotifications]);

  // Mark single notification as read
  const markNotificationRead = useCallback(async (id: string) => {
    try {
      const result = await ultronTuringApi.queryNotifications({ action: 'mark_read', notificationId: id });
      
      if (result.success) {
        // Update local state only if backend operation succeeded
        setAlerts(prev =>
          prev.map(a =>
            a.id === id ? { ...a, acknowledged: true } : a
          )
        );
        logger.log('✅ [AlertContext] Marked notification as read:', id);
      } else {
        logger.error('[AlertContext] Failed to mark notification as read:', result.error);
      }
    } catch (error) {
      logger.error('[AlertContext] Failed to mark notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllNotificationsRead = useCallback(async () => {
    try {
      const result = await ultronTuringApi.queryNotifications({ action: 'mark_all_read' });
      
      if (result.success) {
        // Update local state only if backend operation succeeded
        setAlerts(prev =>
          prev.map(a => ({ ...a, acknowledged: true }))
        );
        logger.log('✅ [AlertContext] Marked all notifications as read');
      } else {
        logger.error('[AlertContext] Failed to mark all notifications as read:', result.error);
      }
    } catch (error) {
      logger.error('[AlertContext] Failed to mark all notifications as read:', error);
    }
  }, []);

  // Delete notification from Firestore
  const deleteNotification = useCallback(async (id: string) => {
    try {
      const result = await ultronTuringApi.queryNotifications({ action: 'delete', notificationId: id });
      
      if (result.success) {
        // Remove from local state and mark as dismissed only if backend operation succeeded
        dismissedAlertIdsRef.current.add(id);
        saveDismissedAlertIds(dismissedAlertIdsRef.current);
        setAlerts(prev => prev.filter(a => a.id !== id));
        logger.log('🗑️ [AlertContext] Deleted notification:', id);
      } else {
        logger.error('[AlertContext] Failed to delete notification:', result.error);
      }
    } catch (error) {
      logger.error('[AlertContext] Failed to delete notification:', error);
    }
  }, []);

  // Load notifications on mount when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadNotifications();
    }
  }, [isAuthenticated, loadNotifications]);

  // Auto-poll for new notifications when authenticated
  // This acts as a backup to WebSocket real-time updates
  useEffect(() => {
    if (!isAuthenticated) return;

    const POLL_INTERVAL = 30000; // 30 seconds

    const pollNotifications = () => {
      // Poll for new notifications as backup to WebSocket
      loadNotifications().catch(err => {
        logger.error('[AlertContext] Auto-poll failed:', err);
      });
    };

    // Set up periodic polling (skip initial poll since loadNotifications already called above)
    const intervalId = setInterval(pollNotifications, POLL_INTERVAL);

    logger.log('[AlertContext] Notification auto-poll started (every 30s)');

    return () => {
      clearInterval(intervalId);
      logger.log('[AlertContext] Notification auto-poll stopped');
    };
  }, [isAuthenticated, loadNotifications]);

  // Calculate unread count
  const unreadCount = alerts.filter(a => !a.acknowledged).length;

  // Request notification permission on mount (optional - can be triggered by user action)
  useEffect(() => {
    // Only request if not denied and not already granted
    if ('Notification' in window && Notification.permission === 'default') {
      // Don't auto-request, let user trigger it
      logger.log('[AlertContext] Notification permission is default, waiting for user action');
    }
  }, []);

  const value: AlertContextType = {
    alerts,
    unreadCount,
    isConnected,
    isLoading,
    dismissAlert,
    acknowledgeAlert,
    clearAllAlerts,
    requestNotificationPermission,
    reloadNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    deleteNotification
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
};

export default AlertContext;
